import type { HomepageContent } from "@whitehaven/contracts/homepage";

export interface HomepageContentViewProps {
  readonly content: HomepageContent;
  readonly mode?: "published" | "editor";
  readonly actions?: "interactive" | "inactive";
  readonly disabled?: boolean;
  readonly onPlainTextChange?: (path: string, value: string) => void;
  readonly semanticContext?: "document" | "embedded";
}

function SectionHeading({
  eyebrow,
  heading,
  body,
}: {
  readonly eyebrow: string;
  readonly heading: string;
  readonly body: string;
}) {
  return (
    <header className="v21-homepage__section-heading">
      <div>
        <p className="v21-homepage__eyebrow">{eyebrow}</p>
        <h2>{heading}</h2>
      </div>
      <p>{body}</p>
    </header>
  );
}

function AddedSection({
  section,
  interactive,
}: {
  readonly section: HomepageContent["addedSections"][number];
  readonly interactive: boolean;
}) {
  const heading = (
    <SectionHeading
      body={section.body}
      eyebrow={section.eyebrow}
      heading={section.heading}
    />
  );
  const presentation = {
    "data-accent": section.presentation.accent,
    "data-alignment": section.presentation.alignment,
    "data-layout": section.presentation.layout,
    "data-spacing": section.presentation.spacing,
  } as const;
  if (section.kind === "cards") {
    return (
      <section
        className="v21-homepage__section v21-homepage__added-section"
        id={section.id}
        {...presentation}
      >
        {heading}
        <div className="v21-homepage__card-grid">
          {section.cards.map((card) => (
            <article key={card.title}>
              <h3>{card.title}</h3>
              <p>{card.body}</p>
            </article>
          ))}
        </div>
      </section>
    );
  }
  if (section.kind === "faq") {
    return (
      <section
        className="v21-homepage__section v21-homepage__added-section v21-homepage__added-faq"
        id={section.id}
        {...presentation}
      >
        {heading}
        <div>
          {section.items.map((item) => (
            <details key={item.question}>
              <summary>{item.question}</summary>
              <p>{item.answer}</p>
            </details>
          ))}
        </div>
      </section>
    );
  }
  if (section.kind === "richText") {
    return (
      <section
        className="v21-homepage__section v21-homepage__added-section v21-homepage__rich-text"
        id={section.id}
        {...presentation}
      >
        {heading}
        {section.paragraphs.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </section>
    );
  }
  return (
    <section
      className={`v21-homepage__section v21-homepage__added-${section.kind}`}
      id={section.id}
      {...presentation}
    >
      {heading}
      <div className="v21-homepage__actions">
        {interactive ? (
          <a href={section.actionHref}>{section.actionLabel}</a>
        ) : (
          <span>{section.actionLabel}</span>
        )}
      </div>
    </section>
  );
}

export function HomepageContentView({
  content,
  actions = "inactive",
  semanticContext = "document",
}: HomepageContentViewProps) {
  const interactive = actions === "interactive";
  const Heading = semanticContext === "embedded" ? "h2" : "h1";

  return (
    <div
      className="v21-homepage"
      data-buttons={content.theme.buttons}
      data-cards={content.theme.cards}
      data-homepage-source="v2.1"
      data-palette={content.theme.palette}
      data-spacing={content.theme.spacing}
      data-typography={content.theme.typography}
    >
      <section className="v21-homepage__hero">
        <div>
          <p className="v21-homepage__eyebrow">{content.hero.eyebrow}</p>
          <Heading>
            {content.hero.headingLead} <span>{content.hero.headingAccent}</span>
          </Heading>
          <p className="v21-homepage__lead">{content.hero.lead}</p>
          <div className="v21-homepage__actions">
            {interactive ? (
              <>
                <a href="/book">{content.hero.primaryActionLabel}</a>
                <a href="#services">{content.hero.secondaryActionLabel}</a>
              </>
            ) : (
              <>
                <span>{content.hero.primaryActionLabel}</span>
                <span>{content.hero.secondaryActionLabel}</span>
              </>
            )}
          </div>
          <ul className="v21-homepage__inline-list">
            {content.hero.trustItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
        <article className="v21-homepage__visit-card">
          <header>
            <strong>{content.hero.visitCard.title}</strong>
            <span>{content.hero.visitCard.status}</span>
          </header>
          {content.hero.visitCard.steps.map((step, index) => (
            <div key={step.title}>
              <b>{index + 1}</b>
              <p>
                <strong>{step.title}</strong>
                <span>{step.body}</span>
              </p>
            </div>
          ))}
        </article>
      </section>

      <ul className="v21-homepage__trust-strip">
        {content.hero.trustStrip.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>

      <section className="v21-homepage__section" id="services">
        <SectionHeading {...content.services} />
        <p className="v21-homepage__catalog-note">
          Service cards and booking modes are supplied by the Services catalog.
        </p>
      </section>

      <section className="v21-homepage__section v21-homepage__section--dark">
        <SectionHeading {...content.bookingSteps} />
        <div className="v21-homepage__card-grid">
          {content.bookingSteps.steps.map((step, index) => (
            <article key={step.title}>
              <b>{index + 1}</b>
              <h3>{step.title}</h3>
              <p>{step.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="v21-homepage__section">
        <SectionHeading {...content.packages} />
        <p className="v21-homepage__catalog-note">
          Package names, prices, credits, and eligibility are supplied by the
          Packages catalog.
        </p>
      </section>

      <section className="v21-homepage__section v21-homepage__section--soft">
        <SectionHeading {...content.careBoundary} />
        <div className="v21-homepage__card-grid v21-homepage__card-grid--two">
          {content.careBoundary.cards.map((card) => (
            <article key={card.label}>
              <span>{card.label}</span>
              <h3>{card.title}</h3>
              <p>{card.body}</p>
              <ul>
                {card.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="v21-homepage__section">
        <SectionHeading {...content.guides} />
        <div className="v21-homepage__card-grid">
          {content.guides.cards.map((card) => (
            <article key={card.title}>
              <span>{card.eyebrow}</span>
              <h3>{card.title}</h3>
              <p>{card.body}</p>
              <footer>
                <small>{card.meta}</small>
                <b>{card.actionLabel}</b>
              </footer>
            </article>
          ))}
        </div>
      </section>

      <section className="v21-homepage__section v21-homepage__faq">
        <div>
          <p className="v21-homepage__eyebrow">{content.faq.eyebrow}</p>
          <h2>{content.faq.heading}</h2>
          <p>{content.faq.body}</p>
          <b>{content.faq.actionLabel}</b>
        </div>
        <div>
          {content.faq.items.map((item, index) => (
            <details key={item.question} open={index === 0}>
              <summary>{item.question}</summary>
              <p>{item.answer}</p>
            </details>
          ))}
        </div>
      </section>

      {content.addedSections.map((section) => (
        <AddedSection
          interactive={interactive}
          key={section.id}
          section={section}
        />
      ))}

      <section className="v21-homepage__section v21-homepage__contact">
        <div>
          <p className="v21-homepage__eyebrow">{content.contact.eyebrow}</p>
          <h2>{content.contact.heading}</h2>
          <p>{content.contact.body}</p>
          <a href={`mailto:${content.contact.email}`}>
            {content.contact.email}
          </a>
          <p>{content.contact.privacyNotice}</p>
          <p>{content.contact.availabilityNotice}</p>
        </div>
        <article>
          <b>{content.contact.formHelp}</b>
          <p>{content.contact.consentLabel}</p>
          <span>{content.contact.actionLabel}</span>
        </article>
      </section>

      <footer className="v21-homepage__footer">
        <p>{content.footer.description}</p>
        <small>{content.footer.legalLine}</small>
      </footer>
    </div>
  );
}
